import { useRef, useState } from "react";
import { ImagePlus, Star, X, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";

export const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
};

const MAX = 12;

export default function PhotoUploader({ photos, cover, onChange }) {
  const input = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [drag, setDrag] = useState(false);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    const room = MAX - photos.length;
    if (files.length > room) toast.error(`You can add up to ${MAX} photos. ${room} spot${room === 1 ? "" : "s"} left.`);
    const batch = files.slice(0, room);
    if (!batch.length) return;
    setUploading(batch.length);
    let current = [...photos];
    for (const f of batch) {
      try {
        const path = await uploadFile(f);
        current = [...current, path];
        onChange(current, cover || current[0]);
      } catch (e) {
        toast.error(`${f.name}: ${errMsg(e)}`);
      }
      setUploading((n) => n - 1);
    }
  };

  const remove = (p) => {
    const next = photos.filter((x) => x !== p);
    onChange(next, cover === p ? next[0] || "" : cover);
  };
  const move = (i, dir) => {
    const next = [...photos];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next, cover);
  };

  return (
    <div data-testid="photo-uploader">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {photos.map((p, i) => (
          <div key={p} className={`group relative aspect-square rounded-lg overflow-hidden border-2 bg-zinc-100 ${cover === p ? "border-zinc-950" : "border-transparent"}`} data-testid={`photo-item-${i}`}>
            <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
            {cover === p && <span className="absolute top-1.5 left-1.5 bg-zinc-950 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">Cover</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between items-center p-1.5 bg-gradient-to-t from-black/60 to-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, -1)} className="w-6 h-6 rounded bg-white/90 flex items-center justify-center" aria-label="Move left" data-testid={`photo-move-left-${i}`}><ChevronLeft size={13} /></button>
                <button type="button" onClick={() => move(i, 1)} className="w-6 h-6 rounded bg-white/90 flex items-center justify-center" aria-label="Move right" data-testid={`photo-move-right-${i}`}><ChevronRight size={13} /></button>
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => onChange(photos, p)} className="w-6 h-6 rounded bg-white/90 flex items-center justify-center" title="Make cover photo" data-testid={`photo-set-cover-${i}`}><Star size={12} className={cover === p ? "fill-zinc-950" : ""} /></button>
                <button type="button" onClick={() => remove(p)} className="w-6 h-6 rounded bg-white/90 flex items-center justify-center text-red-600" title="Remove" data-testid={`photo-remove-${i}`}><X size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {Array.from({ length: uploading }).map((_, i) => (
          <div key={`u${i}`} className="aspect-square rounded-lg bg-zinc-100 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-400" size={20} /></div>
        ))}
        {photos.length + uploading < MAX && (
          <button type="button" onClick={() => input.current?.click()} data-testid="photo-add-button"
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
            className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1.5 text-zinc-500 hover:text-zinc-900 hover:border-zinc-400 transition-colors ${drag ? "border-zinc-950 bg-zinc-50" : "border-zinc-300"} ${photos.length === 0 ? "col-span-2 sm:col-span-4 aspect-auto py-10" : ""}`}>
            <ImagePlus size={22} />
            <span className="text-xs font-medium">{photos.length === 0 ? "Add photos — click or drag them here" : "Add"}</span>
          </button>
        )}
      </div>
      <p className="text-xs text-zinc-500 mt-3">{photos.length}/{MAX} photos · Click the star to choose the cover photo · All colors share these photos</p>
      <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="photo-file-input" />
    </div>
  );
}
